import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gavel, Send, RefreshCw, ChevronLeft, ChevronRight, Music, Sparkles, Heart, Star, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import JudgeFeedbackPanel from "../components/JudgeFeedbackPanel";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";

// Slider-based score control for mobile-friendly decimal scoring
const ScoreSlider = ({ label, icon, color, value, onChange }) => {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ color }}>{icon}</span>
          <span className="text-white text-sm">{label}</span>
        </div>
        <span className="font-bold text-xl min-w-[3rem] text-right" style={{ color }}>
          {value.toFixed(1)}
        </span>
      </div>
      <div className="px-1">
        <Slider
          value={[value]}
          onValueChange={([v]) => onChange(Math.round(v * 10) / 10)}
          min={5}
          max={10}
          step={0.1}
          className="touch-manipulation [&_[role=slider]]:h-7 [&_[role=slider]]:w-7 [&_[role=slider]]:border-2"
          data-testid={`slider-${label.toLowerCase()}`}
        />
        <div className="flex justify-between mt-1">
          <span className="text-[#52525B] text-[10px]">5</span>
          <span className="text-[#52525B] text-[10px]">7.5</span>
          <span className="text-[#52525B] text-[10px]">10</span>
        </div>
      </div>
    </div>
  );
};

// Criteria configs for JudgePage
const DEFAULT_CRITERIA_MAP = {
  overall: { label: "Overall", icon: <Star className="w-4 h-4" />, color: "#FFD700" },
  connection: { label: "Connection", icon: <Heart className="w-4 h-4" />, color: "#FF007F" },
  technique: { label: "Technique", icon: <Sparkles className="w-4 h-4" />, color: "#00E5FF" },
  musicality: { label: "Musicality", icon: <Music className="w-4 h-4" />, color: "#FFD700" },
  presence: { label: "Presence", icon: <Star className="w-4 h-4" />, color: "#A855F7" }
};

const CUSTOM_COLORS = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"];

const getCriteriaConfig = (criterionName) => {
  if (DEFAULT_CRITERIA_MAP[criterionName]) return DEFAULT_CRITERIA_MAP[criterionName];
  const idx = criterionName.length % CUSTOM_COLORS.length;
  return { label: criterionName.charAt(0).toUpperCase() + criterionName.slice(1), icon: <Star className="w-4 h-4" />, color: CUSTOM_COLORS[idx] };
};

// Dancer scoring card
const DancerScoreCard = ({ dancer, scores, onScoreChange, isExpanded, onToggle, roundConfig }) => {
  const isLeader = dancer.role === "Leader";
  const borderColor = isLeader ? "#FFD700" : "#FF007F";
  const criteria = roundConfig?.criteria || ["overall"];
  const avgScore = criteria.length > 0 
    ? criteria.reduce((sum, c) => sum + (scores?.[c] || 5), 0) / criteria.length 
    : 0;

  return (
    <div 
      className={`bg-[#18181b] rounded-xl overflow-hidden border-l-4 ${isExpanded ? 'ring-2 ring-white/20' : ''}`}
      style={{ borderLeftColor: borderColor }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between text-left touch-manipulation"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold"
            style={{ backgroundColor: `${borderColor}15`, color: borderColor }}
          >
            #{dancer.bib_number || '?'}
          </div>
          <div>
            <p className="text-white font-medium">{dancer.name}</p>
            <p className="text-xs" style={{ color: borderColor }}>{dancer.role}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-xl" style={{ color: borderColor }}>{avgScore.toFixed(1)}</p>
          <p className="text-[#52525B] text-xs">avg</p>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-4 border-t border-white/10"
          >
            <div className="pt-3">
              {criteria.map(criterion => {
                const cfg = getCriteriaConfig(criterion);
                return (
                  <ScoreSlider
                    key={criterion}
                    label={cfg.label}
                    icon={cfg.icon}
                    color={cfg.color}
                    value={scores?.[criterion] || 5}
                    onChange={(v) => onScoreChange(criterion, v)}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function JudgePage() {
  const { judgeId } = useParams();
  const [judge, setJudge] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [festivalLogo, setFestivalLogo] = useState(null);
  const [heats, setHeats] = useState([]);
  const [currentHeatIndex, setCurrentHeatIndex] = useState(0);
  const [dancers, setDancers] = useState([]);
  const [scores, setScores] = useState({});
  const [expandedDancer, setExpandedDancer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [roundConfig, setRoundConfig] = useState({ scoring_mode: "single", judging_mode: "full", criteria: ["overall"], score_min: 5, score_max: 10 });
  const [submittedHeats, setSubmittedHeats] = useState(new Set());
  const [showHeatTransition, setShowHeatTransition] = useState(false);
  const [view, setView] = useState("scoring"); // "scoring" | "feedback"
  const prevActiveHeatIdRef = useRef(null);
  const retryRef = useRef(null);

  const currentHeat = heats[currentHeatIndex];
  const activeHeat = heats.find(h => h.status === "active");
  const allHeatsInRoundCompleted = heats.length > 0 && heats.every(h => h.status === "completed");
  const allHeatsSubmittedByMe = heats.length > 0 && heats.every(h => submittedHeats.has(h.heat_id));

  // Show 2-3s transition when organizer activates a NEW heat
  useEffect(() => {
    if (!activeHeat) {
      prevActiveHeatIdRef.current = null;
      return;
    }
    if (prevActiveHeatIdRef.current && prevActiveHeatIdRef.current !== activeHeat.heat_id) {
      setShowHeatTransition(true);
      const t = setTimeout(() => setShowHeatTransition(false), 2500);
      return () => clearTimeout(t);
    }
    prevActiveHeatIdRef.current = activeHeat.heat_id;
  }, [activeHeat?.heat_id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Get round name from competition rounds array
  const getRoundName = () => {
    if (!competition?.rounds || !currentHeat) return "";
    const roundIndex = currentHeat.round_number - 1;
    return competition.rounds[roundIndex] || `Round ${currentHeat.round_number}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const judgeRes = await axios.get(`${API}/judges/${judgeId}`);
      setJudge(judgeRes.data);

      const compRes = await axios.get(`${API}/competitions/${judgeRes.data.competition_id}`);
      setCompetition(compRes.data);

      // Fetch event/festival logo (best-effort, public endpoint)
      if (compRes.data?.festival_id) {
        try {
          const logoRes = await axios.get(`${API}/festivals/${compRes.data.festival_id}/logo`);
          setFestivalLogo(logoRes.data?.logo_base64 || null);
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.debug('no festival logo:', e.message);
        }
      }


      const currentRound = compRes.data.current_round || 1;
      try {
        const configRes = await axios.get(`${API}/competitions/${judgeRes.data.competition_id}/round-config/${currentRound}`);
        setRoundConfig(configRes.data);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error("Round config not found, using default:", e.message); }

      const heatsRes = await axios.get(`${API}/heats?competition_id=${judgeRes.data.competition_id}&round_number=${currentRound}`);
      setHeats(heatsRes.data);

      // Reset per-round judge state when round changes
      setSubmittedHeats(prev => {
        // Keep only heats that still exist in this round
        const roundHeatIds = new Set(heatsRes.data.map(h => h.heat_id));
        const filtered = new Set();
        prev.forEach(id => { if (roundHeatIds.has(id)) filtered.add(id); });
        return filtered;
      });

      const activeIndex = heatsRes.data.findIndex(h => h.status === "active");
      if (activeIndex !== -1) {
        setCurrentHeatIndex(activeIndex);
      }
      setConnecting(false);
      clearInterval(retryRef.current);
    } catch (error) {
      if (!error.response) {
        setConnecting(true);
        return;
      }
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [judgeId]);

  useEffect(() => {
    fetchData();
    retryRef.current = setInterval(() => {
      if (connecting) fetchData();
    }, 3000);
    return () => clearInterval(retryRef.current);
  }, [fetchData, connecting]);

  const fetchHeatDancers = useCallback(async () => {
    if (!currentHeat) return;

    try {
      // Get all dancers in this heat
      const res = await axios.get(`${API}/heats/${currentHeat.heat_id}/dancers`);
      let heatDancers = res.data.dancers || [];
      
      // Filter dancers by judge role when role-based judging
      // (judge_dancing heats bypass role restriction — free voting per organizer instructions)
      if (roundConfig.judging_mode === "role_based" && judge?.judge_role && !currentHeat.judge_dancing) {
        heatDancers = heatDancers.filter(d => d.role === judge.judge_role);
      }
      
      setDancers(heatDancers);

      // ALWAYS reset scores to 5 for this heat - fresh start per heat
      const freshScores = {};
      heatDancers.forEach(dancer => {
        const defaults = {};
        (roundConfig.criteria || ["overall"]).forEach(c => { defaults[c] = 5; });
        freshScores[dancer.dancer_id] = defaults;
      });
      setScores(freshScores);

      // Expand first dancer if none expanded
      if (heatDancers.length > 0 && !expandedDancer) {
        setExpandedDancer(heatDancers[0].dancer_id);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching heat dancers:", error);
    }
  }, [currentHeat?.heat_id]);

  useEffect(() => {
    if (!connecting) {
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchData, connecting]);

  useEffect(() => {
    if (currentHeat) {
      fetchHeatDancers();
    }
  }, [currentHeat?.heat_id]);

  const updateScore = (dancerId, category, value) => {
    setScores(prev => ({
      ...prev,
      [dancerId]: {
        ...prev[dancerId],
        [category]: value
      }
    }));
  };

  const handleSubmitAll = async () => {
    if (!currentHeat || dancers.length === 0) return;

    setSubmitting(true);
    try {
      const promises = dancers.map(dancer =>
        axios.post(`${API}/scores?judge_id=${judgeId}`, {
          heat_id: currentHeat.heat_id,
          dancer_id: dancer.dancer_id,
          criteria_scores: scores[dancer.dancer_id] || {}
        })
      );

      await Promise.all(promises);
      toast.success(`Scores submitted for Heat ${currentHeat.heat_number}!`);

      // Track that this judge has submitted for this heat
      setSubmittedHeats(prev => {
        const next = new Set(prev);
        next.add(currentHeat.heat_id);
        return next;
      });

      // Move to next heat
      if (currentHeatIndex < heats.length - 1) {
        setCurrentHeatIndex(currentHeatIndex + 1);
        setExpandedDancer(null);
      } else {
        toast.success("All heats scored!");
      }
    } catch (error) {
      toast.error("Failed to submit scores");
    } finally {
      setSubmitting(false);
    }
  };

  const leaders = dancers.filter(d => d.role === "Leader").sort((a, b) => (a.bib_number || 0) - (b.bib_number || 0));
  const followers = dancers.filter(d => d.role === "Follower").sort((a, b) => (a.bib_number || 0) - (b.bib_number || 0));

  if (loading || connecting) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-4">
            <Gavel className="w-8 h-8 text-[#FFD700]" />
          </div>
          <div className="w-10 h-10 border-3 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white font-bold mb-1">{connecting ? "Connecting..." : "Loading..."}</p>
          {connecting && <p className="text-[#A1A1AA] text-sm">Please wait a moment</p>}
        </div>
      </div>
    );
  }

  if (!judge) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="text-center">
          <Gavel className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
          <h1 className="text-xl text-white mb-2">Judge Not Found</h1>
          <p className="text-[#A1A1AA]">This link may be invalid or expired</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      {/* Header */}
      <header className="glass px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gavel className="w-5 h-5 text-[#FFD700]" />
            <span className="text-white font-medium">{judge.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-[#A1A1AA] text-xs block">{competition?.category}</span>
              {getRoundName() && (
                <span className="text-[#FFD700] text-sm font-bold">{getRoundName()}</span>
              )}
            </div>
            <div className="px-3 py-1 bg-[#FFD700]/10 rounded-lg">
              <span className="text-[#FFD700] text-xs font-bold uppercase tracking-wider">
                {getRoundName() || "Scoring"}
              </span>
            </div>
            <div className="flex bg-white/5 rounded-lg p-0.5" data-testid="judge-view-toggle">
              <button
                onClick={() => setView("scoring")}
                className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
                  view === "scoring" ? "bg-[#FFD700] text-black" : "text-white/70"
                }`}
                data-testid="judge-view-scoring"
              >
                <Gavel className="w-3 h-3 inline mr-1" /> Score
              </button>
              <button
                onClick={() => setView("feedback")}
                className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
                  view === "feedback" ? "bg-[#FFD700] text-black" : "text-white/70"
                }`}
                data-testid="judge-view-feedback"
              >
                <MessageSquare className="w-3 h-3 inline mr-1" /> Feedback
              </button>
            </div>
            <button onClick={fetchData} className="p-2 hover:bg-white/5 rounded-lg">
              <RefreshCw className="w-4 h-4 text-[#52525B]" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 pb-32 overflow-y-auto">
        {view === "feedback" ? (
          <JudgeFeedbackPanel api={API} judge={judge} competition={competition} />
        ) : showHeatTransition ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]" data-testid="heat-transition">
            <div className="text-center">
              <div className="w-24 h-24 rounded-3xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Gavel className="w-12 h-12 text-[#FFD700]" />
              </div>
              <h2 className="text-white font-['Playfair_Display'] text-3xl font-bold mb-2">Get ready!</h2>
              <p className="text-[#FFD700] text-xl">Next heat is starting…</p>
            </div>
          </div>
        ) : allHeatsInRoundCompleted ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]" data-testid="round-complete-waiting">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-6">
                <Gavel className="w-10 h-10 text-[#FFD700]" />
              </div>
              <h2 className="text-white font-['Playfair_Display'] text-2xl font-bold mb-3">Round complete</h2>
              <p className="text-[#A1A1AA] mb-4">
                All heats of {getRoundName() || "this round"} are scored.
              </p>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/10 rounded-full border border-[#F59E0B]/20">
                <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse"></span>
                <span className="text-[#F59E0B] text-sm font-medium">Waiting for organizer to start the next round</span>
              </span>
            </div>
          </div>
        ) : allHeatsSubmittedByMe && !activeHeat ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]" data-testid="all-scores-submitted">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
                <Send className="w-10 h-10 text-[#10B981]" />
              </div>
              <h2 className="text-white font-['Playfair_Display'] text-2xl font-bold mb-3">All scores submitted</h2>
              <p className="text-[#A1A1AA] mb-4">
                Thanks! Your scores for this round are saved.
              </p>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/10 rounded-full border border-[#F59E0B]/20">
                <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse"></span>
                <span className="text-[#F59E0B] text-sm font-medium">Wait for the organizer to set up the next heat / round</span>
              </span>
            </div>
          </div>
        ) : !currentHeat ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#A1A1AA]">Waiting for heat to start...</p>
              <p className="text-[#52525B] text-sm mt-2">The organizer will activate the next heat</p>
            </div>
          </div>
        ) : currentHeat.status !== "active" ? (
          <div className="flex-1 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center mx-auto mb-6">
                <Gavel className="w-10 h-10 text-[#FFD700]" />
              </div>
              <h2 className="text-white font-['Playfair_Display'] text-2xl font-bold mb-2">Heat {currentHeat.heat_number}</h2>
              <p className="text-[#A1A1AA] mb-2">Waiting for organizer to start this heat</p>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/10 rounded-full border border-[#F59E0B]/20">
                <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse"></span>
                <span className="text-[#F59E0B] text-sm font-medium">Standby</span>
              </span>
            </div>
          </div>
        ) : (
          <>
            {/* Heat Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCurrentHeatIndex(Math.max(0, currentHeatIndex - 1)); setExpandedDancer(null); }}
                disabled={currentHeatIndex === 0}
                className="text-[#A1A1AA] disabled:opacity-30"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              {/* J&J logo (left of heat pill) */}
              <img
                src="/jj-logo.png"
                alt="JackAndJill"
                className="w-16 h-16 md:w-20 md:h-20 object-contain opacity-90 ml-1"
                data-testid="judge-jj-logo"
              />
              <div className="text-center flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF007F]/20 rounded-full">
                  <span className="w-2 h-2 bg-[#FF007F] rounded-full animate-pulse"></span>
                  <span className="text-[#FF007F] font-bold">HEAT {currentHeat.heat_number} / {heats.length}</span>
                </div>
                <p className="text-[#52525B] text-xs mt-1">{dancers.length} dancers</p>
                {currentHeat.judge_dancing && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/15 rounded-full border border-[#FFD700]/40" data-testid="judge-dancing-banner">
                    <Sparkles className="w-3 h-3 text-[#FFD700]" />
                    <span className="text-[#FFD700] text-[11px] font-bold uppercase tracking-wider">
                      Judge Dancing · {currentHeat.showcase_role}s showcase · Free voting
                    </span>
                  </div>
                )}
              </div>
              {/* Event logo (right of heat pill) */}
              {festivalLogo ? (
                <img
                  src={festivalLogo.startsWith('data:') ? festivalLogo : `data:image/png;base64,${festivalLogo}`}
                  alt="Event"
                  className="w-16 h-16 md:w-20 md:h-20 object-contain rounded mr-1"
                  data-testid="judge-event-logo"
                />
              ) : (
                <div className="w-16 h-16 md:w-20 md:h-20 mr-1" />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCurrentHeatIndex(Math.min(heats.length - 1, currentHeatIndex + 1)); setExpandedDancer(null); }}
                disabled={currentHeatIndex === heats.length - 1}
                className="text-[#A1A1AA] disabled:opacity-30"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Leaders Section */}
            {leaders.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-[#FFD700]"></div>
                  <h3 className="text-white font-bold">Leaders ({leaders.length})</h3>
                </div>
                <div className="space-y-2">
                  {leaders.map((dancer) => (
                    <DancerScoreCard
                      key={dancer.dancer_id}
                      dancer={dancer}
                      scores={scores[dancer.dancer_id] || {}}
                      onScoreChange={(cat, val) => updateScore(dancer.dancer_id, cat, val)}
                      isExpanded={expandedDancer === dancer.dancer_id}
                      onToggle={() => setExpandedDancer(expandedDancer === dancer.dancer_id ? null : dancer.dancer_id)}
                      roundConfig={roundConfig}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Followers Section */}
            {followers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-[#FF007F]"></div>
                  <h3 className="text-white font-bold">Followers ({followers.length})</h3>
                </div>
                <div className="space-y-2">
                  {followers.map((dancer) => (
                    <DancerScoreCard
                      key={dancer.dancer_id}
                      dancer={dancer}
                      scores={scores[dancer.dancer_id] || {}}
                      onScoreChange={(cat, val) => updateScore(dancer.dancer_id, cat, val)}
                      isExpanded={expandedDancer === dancer.dancer_id}
                      onToggle={() => setExpandedDancer(expandedDancer === dancer.dancer_id ? null : dancer.dancer_id)}
                      roundConfig={roundConfig}
                    />
                  ))}
                </div>
              </div>
            )}

            {dancers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#A1A1AA]">No dancers in this heat</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Submit Button - only in scoring view when heat is active */}
      {view === "scoring" && currentHeat && currentHeat.status === "active" && dancers.length > 0 && !showHeatTransition && !allHeatsInRoundCompleted && (
        <div className="fixed bottom-0 left-0 right-0 glass-heavy p-4 border-t border-white/10">
          <Button
            className="w-full btn-gold h-14 text-lg font-bold"
            onClick={handleSubmitAll}
            disabled={submitting}
          >
            {submitting ? (
              <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit All Scores for Heat {currentHeat.heat_number}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
